import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../api';
import { ApiError } from '../../../shared/api/apiClient';
import { whatsAppChatsKey } from '../../client/hooks/useWhatsAppData';

// 02.7-settings.md paso a/§4 -- reemplaza los 2 `fetch()` crudos de `UserProfilePanel.jsx` contra
// `/api/contacts/by-phone/:num`. Settings-owned (no `client`) porque nada más en el frontend
// consume `/api/contacts` hoy -- no hay la presión de unificación que sí fuerza §0.1 para
// WhatsApp.
export const contactByPhoneKey = (phone) => ['settings', 'contacts', 'by-phone', phone];

export function useContactByPhone(phone) {
    return useQuery({
        queryKey: contactByPhoneKey(phone),
        queryFn: async () => {
            try {
                return await settingsApi.fetchContactByPhone(phone);
            } catch (err) {
                // El backend responde 404 cuando el contacto no existe todavía (caso normal la
                // primera vez que se abre un chat nuevo, `contactsRouter.js:17-19`) -- `apiClient`
                // lo lanza como `ApiError`, no como un `{success:false}` resuelto. Se trata como
                // "sin contacto" (mismo comportamiento que el `if (res.ok)` que reemplaza), no
                // como un error de query a reintentar/mostrar.
                if (err instanceof ApiError && err.status === 404) return { success: false, contact: null };
                throw err;
            }
        },
        enabled: !!phone,
        retry: false,
        refetchOnWindowFocus: false,
    });
}

// Update optimista -- `UserProfilePanel.jsx` (§5/paso b) ya tenía esta UX instantánea con su
// propio `fetch()` crudo (escribía el estado local antes de que la respuesta volviera); se
// preserva acá vía `onMutate`/`onError` en vez de perderla al migrar a React Query.
export function useUpdateContactByPhone(phone) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => settingsApi.updateContactByPhone(phone, payload),
        onMutate: async (updates) => {
            await qc.cancelQueries({ queryKey: contactByPhoneKey(phone) });
            await qc.cancelQueries({ queryKey: whatsAppChatsKey });

            const previous = qc.getQueryData(contactByPhoneKey(phone));
            const previousChats = qc.getQueryData(whatsAppChatsKey);

            qc.setQueryData(contactByPhoneKey(phone), (prev) => ({
                success: true,
                contact: { ...(prev?.contact || {}), ...updates },
            }));

            if (previousChats) {
                qc.setQueryData(whatsAppChatsKey, (prevChats) => {
                    if (!Array.isArray(prevChats)) return prevChats;
                    return prevChats.map(chat => {
                        if (chat.chat_jid && chat.chat_jid.startsWith(phone)) {
                            return { 
                                ...chat, 
                                name: updates.name !== undefined ? updates.name : chat.name, 
                                avatar_url: updates.avatar_url !== undefined ? updates.avatar_url : chat.avatar_url 
                            };
                        }
                        return chat;
                    });
                });
            }

            return { previous, previousChats };
        },
        onError: (_err, _updates, context) => {
            if (context?.previous) qc.setQueryData(contactByPhoneKey(phone), context.previous);
            if (context?.previousChats) qc.setQueryData(whatsAppChatsKey, context.previousChats);
        },
        onSuccess: (d) => {
            qc.setQueryData(contactByPhoneKey(phone), d);
            qc.invalidateQueries({ queryKey: whatsAppChatsKey });
        },
    });
}
